import React, {useState, useEffect} from 'react'
import InputField from '../input-field/InputField'
import {showSnackbar} from '../../misc/utils'
import ArrowLeftWithBody from '../../assets/icons/ArrowLeftWithBody'
import CheckMark from '../../assets/icons/CheckMark'
import {Specialty, Subject, SubjectFormData} from './types'
import Delete from "../../assets/icons/Delete";
import Modal from "../Modal/Modal";
import {simpleGetter} from '../../actions/common';
import {subjectsApi} from '../../api/api';

interface CreateSubjectModalProps {
    visible: boolean
    setVisible: (visible: boolean) => void
    subjectsList?: Subject[]
    onSuccess?: () => void
}

const mockSpecialties: Specialty[] = [
    {id: '1', name: 'Информатика'},
    {id: '2', name: 'Физика'},
    {id: '3', name: 'Математика'},
]

export default function CreateSubjectModal({
                                               visible,
                                               setVisible,
                                               subjectsList = [],
                                               onSuccess,
                                           }: CreateSubjectModalProps) {
    const [formData, setFormData] = useState<SubjectFormData>({
        name: '',
        disciplineCipher: '',
        disciplineCode: '',
        creditAmount: 0,
        description: '',
        speciality: [],
        prerequisites: [],
        oldNewSubject: [],
    })

    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => {
        if (visible) {
            setIsVisible(true)
        } else {
            const timer = setTimeout(() => setIsVisible(false), 200)
            return () => clearTimeout(timer)
        }
    }, [visible])

    const handleChange = <K extends keyof SubjectFormData>(
        key: K,
        value: SubjectFormData[K]
    ) => {
        setFormData(prev => ({...prev, [key]: value}))
    }

    const toggleMultiSelect = <K extends keyof SubjectFormData>(
        key: K,
        item: SubjectFormData[K] extends (infer U)[] ? U : never,
        list: SubjectFormData[K] extends (infer U)[] ? U[] : never
    ) => {
        const exists = (list as any[]).find((i: any) => i.id === (item as any).id)
        const newList = exists
            ? (list as any[]).filter((i: any) => i.id !== (item as any).id)
            : [...(list as any[]), item]
        handleChange(key, newList as SubjectFormData[K])
    }

    const handleSubmit = async () => {
        if (!isFormValid) return
        setIsSubmitting(true)
        try {
            const prereqIds = formData.prerequisites
                .map((p) => parseInt(String(p.id), 10))
                .filter((n) => !Number.isNaN(n))
            const relatedIds = formData.oldNewSubject
                .map((s) => parseInt(String(s.id), 10))
                .filter((n) => !Number.isNaN(n))
            const res = await simpleGetter(subjectsApi.create, {
                create: {
                    name: formData.name.trim(),
                    discipline_cipher: formData.disciplineCipher.trim(),
                    discipline_code: formData.disciplineCode.trim(),
                    credit_amount: formData.creditAmount,
                    description: formData.description?.trim() ?? '',
                    specialities: formData.speciality.map((s) => s.name),
                    prerequisite_ids: prereqIds,
                    related_subject_ids: relatedIds,
                },
                user_params: { skip_user_params: true },
            })
            if (res?.success && res?.item) {
                showSnackbar('Предмет успешно создан!', 'success')
                setFormData({
                    name: '',
                    disciplineCipher: '',
                    disciplineCode: '',
                    creditAmount: 0,
                    description: '',
                    speciality: [],
                    prerequisites: [],
                    oldNewSubject: [],
                })
                setVisible(false)
                onSuccess?.()
            } else {
                const msg = res?.msg || 'Ошибка при создании предмета'
                showSnackbar(msg, 'error')
            }
        } catch (error) {
            console.error(error)
            const msg = (error as any)?.response?.data?.msg || (error as any)?.message || 'Ошибка при создании предмета'
            showSnackbar(msg, 'error')
        } finally {
            setIsSubmitting(false)
        }
    }


    const isFormValid =
        formData.name.trim() &&
        formData.disciplineCipher.trim() &&
        formData.disciplineCode.trim() &&
        formData.creditAmount > 0

    if (!isVisible) return null

    return (
        <Modal visible={visible} setVisible={setVisible} additionalClassName={'sm: w-4/5 w-3/5'}>
            <div
                // className={`bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto w-full sm:w-4/5 lg:w-3/5 transform transition-all duration-200 ${
                //     visible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
                // }`}
            >
                <div className="flex flex-col p-6 md:p-8">
                    <h2 className="text-2xl text-[#1A1A1A] mb-8 font-semibold">
                        Создание предмета
                    </h2>

                    {/* Основная форма */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Левая колонка */}
                        <div className="flex flex-col gap-4">
                            <InputField
                                type="text"
                                id="subjectName"
                                label="Название дисциплины"
                                value={formData.name}
                                handleChange={e => handleChange('name', e.target.value)}
                                placeholder="Введите название дисциплины"
                                additionalClassName="admin-input placeholder-valley-light-gray"
                            />

                            <div className="flex flex-col sm:flex-row sm:items-end gap-3 w-full">
                                <div className="flex-1">
                                    <InputField
                                        type="text"
                                        id="disciplineCipher"
                                        label="Шифр дисциплины"
                                        value={formData.disciplineCipher}
                                        handleChange={e => handleChange('disciplineCipher', e.target.value)}
                                        placeholder="Введите шифр"
                                        additionalClassName="admin-input w-full placeholder-valley-light-gray"
                                    />
                                </div>
                                <div className="flex-1">
                                    <InputField
                                        type="text"
                                        id="disciplineCode"
                                        label="Код дисциплины"
                                        value={formData.disciplineCode}
                                        handleChange={e => handleChange('disciplineCode', e.target.value)}
                                        placeholder="Введите код"
                                        additionalClassName="admin-input w-full placeholder-valley-light-gray"
                                    />
                                </div>
                                <div className="flex-1">
                                    <InputField
                                        type="text"
                                        id="creditAmount"
                                        label="Кредиты"
                                        value={formData.creditAmount.toString()}
                                        handleChange={e =>
                                            handleChange('creditAmount', Number(e.target.value.replace(/\D/g, '')))
                                        }
                                        placeholder="Введите кредиты дисциплины"
                                        additionalClassName="admin-input w-full placeholder-valley-light-gray"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-valley-gray mb-2">
                                    Описание
                                </label>
                                <textarea
                                    className="w-full resize-none rounded-lg border bg-[#F0F3FA] border-[#F0F3FA] p-2 text-sm focus:ring-2 focus:outline-none"
                                    rows={5}
                                    value={formData.description}
                                    onChange={e => handleChange('description', e.target.value)}
                                    placeholder="Введите описание..."
                                />
                            </div>
                        </div>

                        {/* Правая колонка */}
                        <div className="flex flex-col gap-4">
                            <MultiSelectSection
                                label="Привязка к специальностям"
                                items={mockSpecialties}
                                selected={formData.speciality}
                                onSelect={item =>
                                    toggleMultiSelect('speciality', item, formData.speciality)
                                }
                            />

                            <MultiSelectSection
                                label="Пререквизиты"
                                items={subjectsList}
                                selected={formData.prerequisites}
                                onSelect={item =>
                                    toggleMultiSelect('prerequisites', item, formData.prerequisites)
                                }
                            />

                            <MultiSelectSection
                                label="Старый/новый предмет"
                                items={subjectsList}
                                selected={formData.oldNewSubject}
                                onSelect={item =>
                                    toggleMultiSelect('oldNewSubject', item, formData.oldNewSubject)
                                }
                            />
                        </div>
                    </div>

                    {/* Кнопки */}
                    <div className="flex flex-col sm:flex-row gap-3 mt-8">
                        <button
                            className="w-full sm:w-auto flex-1 rounded-lg flex items-center justify-center bg-valley-gray py-3 text-white transition hover:opacity-90"
                            onClick={() => setVisible(false)}
                        >
                            <ArrowLeftWithBody additionalStyle="text-white"/>
                            <span className="ml-2 font-bold text-[15px]">Вернуться</span>
                        </button>

                        <button
                            className={`w-full sm:w-auto flex-1 rounded-lg flex items-center justify-center bg-valley-main py-3 text-white transition  ${
                                !isFormValid || isSubmitting
                                    ? 'opacity-50 cursor-not-allowed'
                                    : ''
                            }`}
                            disabled={!isFormValid || isSubmitting}
                            onClick={handleSubmit}
                        >
                            <CheckMark additionalStyle="text-white"/>
                            <span className="ml-2 font-bold text-[15px]">
                {isSubmitting ? 'Создание...' : 'Создать предмет'}
              </span>
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

interface MultiSelectSectionProps<T> {
    label: string
    items: T[]
    selected: T[]
    onSelect: (item: T) => void
}

function MultiSelectSection<T extends { id: number | string; name: string }>({
                                                                                 label,
                                                                                 items,
                                                                                 selected,
                                                                                 onSelect,
                                                                             }: MultiSelectSectionProps<T>) {
    return (
        <div>
            <label className="block text-sm font-semibold text-[#5F6B88] mb-2">
                {label}
            </label>

            <div className="flex flex-wrap gap-2 mb-2">
                {selected.map(s => (
                    // <div
                    //     key={s.id}
                    //     className="flex items-center bg-valley-main/15 px-3 py-1 rounded-lg text-sm hover:bg-valley-main/25 cursor-pointer"
                    //     onClick={() => onSelect(s)}
                    // >
                    //     {s.name} ✕
                    // </div>

                    <div
                        key={s.id}
                        className={`flex items-center bg-valley-main/15 text-black px-3 py-1 rounded-lg text-sm hover:bg-valley-main/25`}
                    >
                        <span className="mr-2">{s.name}</span>
                        <button
                            onClick={() => onSelect(s)}
                            className={`hover:bg-valley-red rounded-full p-1`}
                        >
                            <Delete additionalStyle="text-valley-gray hover:text-white w-3 h-3" />
                        </button>
                    </div>
                ))}
            </div>

            <select
                className="w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-valley-main"
                onChange={e => {
                    const id = e.target.value
                    const found = items.find(s => s.id === id)
                    if (found) onSelect(found)
                }}
            >
                <option value="">Выберите...</option>
                {items.map(s => (
                    <option key={s.id} value={s.id}>
                        {s.name}
                    </option>
                ))}
            </select>



        </div>
    )
}
